import { Plus } from 'lucide-react';
import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Botão "+" de estado vazio (spec do card refinado, ago/2026): seção sem
 * conteúdo não renderiza corpo, vira este botão. Borda dashed para diferenciar
 * de ação primária; ganha cor primária no hover.
 *
 * As classes passam por `cn` (tailwind-merge) porque a sidebar tem paleta
 * própria: lá dentro `--color-text-secondary` é cinza escuro sobre chrome
 * escuro, ou seja, invisível. Sem o merge, o override dependeria da ordem em
 * que o Tailwind emitiu as regras no CSS, não da ordem do atributo class.
 *
 * `forwardRef` não é enfeite: sem ele este botão não pode ser usado como
 * `<DropdownMenuTrigger asChild>`. O Radix passa uma ref para o filho e usa
 * essa ref para ancorar o menu; uma função component que não encaminha ref faz
 * o menu simplesmente NÃO ABRIR, sem erro nenhum na tela. Custou uma rodada de
 * "cliquei e não acontece nada" em 16/08/2026.
 */
interface AddButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Troca o "+" por outro ícone. O tamanho continua sendo do chamador. */
  icon?: ReactNode;
}

export const AddButton = forwardRef<HTMLButtonElement, AddButtonProps>(
  ({ children, className = '', icon, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 text-[13px] px-3 py-[5px] rounded-[var(--radius-md)] border border-dashed border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors cursor-pointer',
        className
      )}
      {...props}
    >
      {icon ?? <Plus size={12} className="text-[var(--color-primary)]" />}
      {children}
    </button>
  )
);

AddButton.displayName = 'AddButton';
